import './styles.scss'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { commonMethods } from 'Utils/commonMethods'
import { coffeeReportJsonLinks } from 'Utils/Constants'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import LotWiseMobileView from './LotWiseMobileView'

const LotWiseComparison = ({ selectedMarket, leftDivRef, rightDivRef, scrollFunction, setLoading, mockLotWiseComparisonData, setSelectedMarket }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const fullPath = location.pathname
  const pathSegments = fullPath.split('/')
  const desiredPath = pathSegments[2] || ''
  const userDetails = getLocalUserDetails()
  let menuLock
  const windowSize = window.innerWidth

  if (userDetails && userDetails.menus) {
    const menu = userDetails.menus.find(({ menuUrl }) => menuUrl === desiredPath)

    if (menu) {
      menuLock = menu.menulock
    }
  }
  const { robustaLotWiseComparison, arabicaLotWiseComparison } = coffeeReportJsonLinks
  const { dateFormatBasedOnUser, checkForUserAccess } = commonMethods

  const [fetchedData, setFetchedData] = useState([])
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null)

  // testing purpose

  useEffect(() => {
    setFetchedData(mockLotWiseComparisonData)
  }, [])

  useEffect(() => {
    fetchData()
  }, [selectedMarket])

  useEffect(() => {
    const cleanUp = scrollFunction() || (() => {})

    return () => {
      cleanUp()
    }
  }, [])

  const fetchData = async () => {
    setLoading(true)

    const reportName = selectedMarket === 'Robusta' ? robustaLotWiseComparison : arabicaLotWiseComparison

    try {
      const response = await apiAdapterCoffeeWeb.getDataByReportName({ reportName })

      setFetchedData(response.data)
    } catch (error) {
      // console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleMouseEnter = (index) => {
    setHoveredRowIndex(index)
  }

  const handleMouseLeave = () => {
    setHoveredRowIndex(null)
  }

  return (
    <>
      {windowSize < 768 ? (
        <LotWiseMobileView fetchedData={fetchedData} reportName={selectedMarket === 'Robusta' ? robustaLotWiseComparison : arabicaLotWiseComparison} selectedMarket={selectedMarket} setSelectedMarket={setSelectedMarket} />
      ) : (
        <div className="table-content" id="lot-wise-content" data-testid="lot-wise-table-content">
          <div className="table-content-left" ref={leftDivRef} data-testid="lot-wise-left-table-content">
            <table>
              <thead>
                <tr className="empty-header-row">
                  <th colSpan={4}></th>
                </tr>

                <tr className="table-content-left-header" data-testid="lot-wise-table-content-header">
                  <th>{t('DATES_UPPERCASE')}</th>
                  <th>{t('TERMINAL_LEVEL_UPPERCASE')}</th>
                </tr>

                <tr className="blank-row"></tr>

                <tr style={checkForUserAccess(menuLock)} className="empty-row">
                  <th colSpan={4}></th>
                </tr>

                <tr className="blank-row"></tr>
              </thead>

              <tbody style={checkForUserAccess(menuLock)}>
                {fetchedData?.terminalDetails?.map((item, index) => (
                  <tr key={index} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} className={hoveredRowIndex === index ? 'highlighted' : ''}>
                    <td data-testid="date-value">{dateFormatBasedOnUser(item?.date)}</td>
                    <td data-testid="terminal-level-value">{item?.terminalLevel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-content-right" ref={rightDivRef} data-testid="lot-wise-right-table-content">
            <table>
              <thead>
                <tr className="header-row-top" data-testid="header-row">
                  <th colSpan={4}></th>
                </tr>
                <tr className="table-header-main" data-testid="main-table-header">
                  <th>{t('OPEN_INTEREST')}</th>
                  <th>{t('NET_CHANGE')}</th>
                </tr>
                <tr className="blank-row"></tr>

                <tr className="header-row-bottom" style={checkForUserAccess(menuLock)}>
                  <th colSpan={4}></th>
                </tr>
                <tr className="blank-row"></tr>
              </thead>

              <tbody style={checkForUserAccess(menuLock)}>
                {fetchedData?.terminalDetails?.map((item, index) => (
                  <tr key={index} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} className={hoveredRowIndex === index ? 'highlighted' : ''}>
                    <td className="light-purple" data-testid="open-interest-value">
                      {item?.openInterest.toLocaleString()}
                    </td>
                    <td className={`${item?.netChange >= 0 ? 'positive-color' : 'negative-color'}`} data-testid="net-change-value">
                      {item?.netChange?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fetchedData?.details?.map((data, index) => (
              <table key={index}>
                <thead>
                  <tr className="table-content-right-title" data-testid="right-table-content-title">
                    <th data-testid="label-data" colSpan={data?.label === 'FUND BUYERS' || data?.label === 'SWAP DEALERS' || data?.label === 'OTHER REPORTABLE' || data?.label === 'FUND BUYERS' ? 7 : 6}>
                      {data?.label}
                    </th>
                  </tr>
                  <tr className="table-content-right-row-title">
                    <th className="positive-label">{t('LONG')}</th>
                    <th className="negative-label">{t('SHORT')}</th>
                    <th>{t('TOTAL').toUpperCase()}</th>
                    <th className="positive-label">{t('PERCENTAGE_LONG')}</th>
                    <th className="negative-label">{t('PERCENTAGE_SHORT')}</th>
                    <th>{t('NET')}</th>
                    {(data?.label === 'FUND BUYERS' || data?.label === 'SWAP DEALERS' || data?.label === 'OTHER REPORTABLE' || data?.label === 'FUND BUYERS') && <th className="gold-label">{t('SPREADING')}</th>}
                  </tr>
                  <tr className="blank-row"></tr>

                  <tr style={checkForUserAccess(menuLock)}>
                    <th className="positive-label" data-testid="long-value">
                      {data?.long.toLocaleString()}
                    </th>
                    <th className="negative-label" data-testid="short-value">
                      {data?.short.toLocaleString()}
                    </th>
                    <th data-testid="total-value">{data?.total.toLocaleString()}</th>
                    <th className="positive-label" data-testid="percentage-Long-value">
                      {data?.percentLong?.toFixed(2)}
                    </th>
                    <th className="negative-label" data-testid="percentage-short-value">
                      {data?.percentShort?.toFixed(2)}
                    </th>
                    <th data-testid="net-value">{data?.net.toLocaleString()}</th>
                    {(data?.label === 'FUND BUYERS' || data?.label === 'SWAP DEALERS' || data?.label === 'OTHER REPORTABLE' || data?.label === 'FUND BUYERS') && <th className="gold-label">{data?.spreading.toLocaleString()}</th>}
                  </tr>
                  <tr className="blank-row"></tr>
                </thead>
                <tbody style={checkForUserAccess(menuLock)}>
                  {data?.data?.map((item, index) => (
                    <tr key={index} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} className={hoveredRowIndex === index ? 'highlighted' : ''}>
                      <td className="positive-label" data-testid="long-item">
                        {item?.long.toLocaleString()}
                      </td>
                      <td className="negative-label" data-testid="short-item">
                        {item?.short.toLocaleString()}
                      </td>
                      <td data-testid="total-item">{item?.total.toLocaleString()}</td>
                      <td className="positive-label" data-testid="percentage-long-item">
                        {item?.percentLong?.toFixed(2)}
                      </td>
                      <td className="negative-label" data-testid="percentage-short-item">
                        {item?.percentShort?.toFixed(2)}
                      </td>
                      <td data-testid="net-item">{item?.net.toLocaleString()}</td>
                      {(data?.label === 'FUND BUYERS' || data?.label === 'SWAP DEALERS' || data?.label === 'OTHER REPORTABLE' || data?.label === 'FUND BUYERS') && (
                        <td className="gold-label" data-testid="spreading-item">
                          {item?.spreading.toLocaleString()}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default LotWiseComparison
